import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accessibility,
  ALargeSmall,
  ArrowUpDown,
  Contrast,
  Fingerprint,
  Focus,
  Minus,
  Moon,
  MousePointer2,
  Pause,
  Plus,
  RotateCcw,
  Type,
  Underline,
  Volume2,
  X,
} from "lucide-react";
import {
  applySettings,
  clearSettings,
  DEFAULT_SETTINGS,
  DEFAULT_TEXT_STEP,
  loadSettings,
  saveSettings,
  TEXT_SIZE_STEPS,
} from "@/lib/a11y";

const DISPLAY_TOGGLES = [
  { key: "highContrast", label: "High Contrast", Icon: Contrast, testid: "a11y-high-contrast" },
  { key: "darkMode", label: "Dark Mode", Icon: Moon, testid: "a11y-dark-mode" },
  { key: "reduceMotion", label: "Reduce Motion", Icon: Pause, testid: "a11y-reduce-motion" },
];

const NAVIGATION_TOGGLES = [
  { key: "highlightLinks", label: "Highlight Links", Icon: Underline, testid: "a11y-highlight-links" },
  { key: "strongFocus", label: "Strong Focus Indicators", Icon: Focus, testid: "a11y-focus-indicators" },
  { key: "largeCursor", label: "Larger Cursor", Icon: MousePointer2, testid: "a11y-larger-cursor" },
];

const READING_TOGGLES = [
  { key: "lineSpacing", label: "Line Spacing", Icon: ArrowUpDown, testid: "a11y-line-spacing" },
  { key: "letterSpacing", label: "Letter Spacing", Icon: ALargeSmall, testid: "a11y-letter-spacing" },
];

const Toggle = ({ Icon, label, checked, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    data-testid={testid}
    className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
      checked ? "border-teal/50 bg-teal/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"
    }`}
  >
    <span className="flex items-center gap-2.5">
      <Icon className={`h-4 w-4 shrink-0 ${checked ? "text-teal" : "text-slatemuted"}`} aria-hidden="true" />
      <span className="text-sm font-medium text-slatelight">{label}</span>
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <span
        className={`font-mono text-[11px] uppercase tracking-wider ${checked ? "text-teal" : "text-slatemuted"}`}
        aria-hidden="true"
      >
        {checked ? "On" : "Off"}
      </span>
      <span aria-hidden="true" className={`relative h-5 w-9 rounded-full ${checked ? "bg-teal" : "bg-white/15"}`}>
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-[left] duration-150 ${
            checked ? "left-[18px]" : "left-0.5"
          }`}
        />
      </span>
    </span>
  </button>
);

const SectionLabel = ({ children }) => (
  <h3 className="mb-2 mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-teal first:mt-0">
    {children}
  </h3>
);

export const AccessibilityMenu = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => loadSettings());
  const [speaking, setSpeaking] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  // Apply immediately on every change and persist for future visits.
  // (Skip persisting when everything is at defaults, so Reset truly clears.)
  useEffect(() => {
    applySettings(settings);
    if (JSON.stringify(settings) === JSON.stringify(DEFAULT_SETTINGS)) {
      clearSettings();
    } else {
      saveSettings(settings);
    }
  }, [settings]);

  // Future-ready: an A-ID profile can push preferences via this event.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail && typeof e.detail === "object") {
        setSettings((prev) => ({ ...prev, ...e.detail }));
      }
    };
    window.addEventListener("a11y:apply", handler);
    return () => window.removeEventListener("a11y:apply", handler);
  }, []);

  // Stop any speech when the component unmounts.
  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  // Focus the panel on open; close on Escape and on outside interaction.
  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.querySelector("button")?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const update = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const changeTextStep = useCallback((delta) => {
    setSettings((prev) => ({
      ...prev,
      textStep: Math.min(TEXT_SIZE_STEPS.length - 1, Math.max(0, prev.textStep + delta)),
    }));
  }, []);

  const resetAll = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    clearSettings();
    setSettings({ ...DEFAULT_SETTINGS });
  }, []);

  const toggleReadAloud = useCallback(() => {
    if (!ttsSupported) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const root = document.querySelector("main") || document.body;
    // Cap length so very long pages can't create a runaway speech queue.
    const text = (root.innerText || "").replace(/\s+/g, " ").trim().slice(0, 8000);
    if (!text) return;
    const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
    const chunks = [];
    let current = "";
    for (const s of sentences) {
      if ((current + s).length > 240) {
        if (current) chunks.push(current);
        current = s;
      } else {
        current += s;
      }
    }
    if (current) chunks.push(current);
    window.speechSynthesis.cancel();
    chunks.forEach((chunk, i) => {
      const utterance = new SpeechSynthesisUtterance(chunk);
      if (i === chunks.length - 1) utterance.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
    });
    setSpeaking(true);
  }, [speaking, ttsSupported]);

  const textPct = TEXT_SIZE_STEPS[settings.textStep];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close accessibility settings" : "Open accessibility settings"}
        aria-expanded={open}
        aria-controls="accessibility-panel"
        data-testid="accessibility-open-button"
        className="fixed bottom-5 right-5 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-teal text-navy-900 shadow-[0_8px_30px_-6px_rgba(0,210,180,0.55)] ring-2 ring-white/20 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan"
      >
        <Accessibility className="h-7 w-7" aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id="accessibility-panel"
          role="dialog"
          aria-label="Accessibility settings"
          data-testid="accessibility-panel"
          className="a11y-panel fixed bottom-24 right-3 z-[90] max-h-[72vh] w-[min(92vw,340px)] overflow-y-auto rounded-2xl border border-white/12 bg-navy-800/95 p-4 shadow-2xl backdrop-blur-xl sm:right-5"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal/15">
                <Accessibility className="h-5 w-5 text-teal" aria-hidden="true" />
              </span>
              <div>
                <h2 className="font-display text-base font-bold text-white">Accessibility</h2>
                <p className="text-xs text-slatemuted">Adjust this website to suit you.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                buttonRef.current?.focus();
              }}
              aria-label="Close accessibility settings"
              data-testid="accessibility-close-button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slatemuted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <SectionLabel>Text Size</SectionLabel>
          <div
            className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
            data-testid="a11y-text-size-control"
          >
            <span className="flex items-center gap-2.5 text-sm font-medium text-slatelight">
              <Type className="h-4 w-4 text-slatemuted" aria-hidden="true" /> Text Size
            </span>
            <span className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => changeTextStep(-1)}
                disabled={settings.textStep === 0}
                aria-label="Decrease text size"
                data-testid="a11y-text-decrease"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slatelight transition-colors hover:border-teal/50 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal disabled:opacity-35"
              >
                <Minus className="h-4 w-4" aria-hidden="true" />
              </button>
              <span
                className="w-14 text-center font-mono text-xs text-teal"
                aria-live="polite"
                data-testid="a11y-text-size-value"
              >
                {textPct}%
              </span>
              <button
                type="button"
                onClick={() => changeTextStep(1)}
                disabled={settings.textStep === TEXT_SIZE_STEPS.length - 1}
                aria-label="Increase text size"
                data-testid="a11y-text-increase"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slatelight transition-colors hover:border-teal/50 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal disabled:opacity-35"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => update("textStep", DEFAULT_TEXT_STEP)}
                aria-label="Reset text size"
                data-testid="a11y-text-reset"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slatelight transition-colors hover:border-teal/50 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          </div>

          <SectionLabel>Display</SectionLabel>
          <div className="space-y-2">
            {DISPLAY_TOGGLES.map(({ key, label, Icon, testid }) => (
              <Toggle
                key={key}
                Icon={Icon}
                label={label}
                checked={Boolean(settings[key])}
                onChange={() => update(key, !settings[key])}
                testid={testid}
              />
            ))}
          </div>

          <SectionLabel>Navigation</SectionLabel>
          <div className="space-y-2">
            {NAVIGATION_TOGGLES.map(({ key, label, Icon, testid }) => (
              <Toggle
                key={key}
                Icon={Icon}
                label={label}
                checked={Boolean(settings[key])}
                onChange={() => update(key, !settings[key])}
                testid={testid}
              />
            ))}
          </div>

          <SectionLabel>Reading</SectionLabel>
          <div className="space-y-2">
            {READING_TOGGLES.map(({ key, label, Icon, testid }) => (
              <Toggle
                key={key}
                Icon={Icon}
                label={label}
                checked={Boolean(settings[key])}
                onChange={() => update(key, !settings[key])}
                testid={testid}
              />
            ))}
          </div>

          <SectionLabel>Audio</SectionLabel>
          <button
            type="button"
            onClick={toggleReadAloud}
            disabled={!ttsSupported}
            aria-pressed={speaking}
            data-testid="a11y-read-aloud"
            className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal disabled:opacity-50 ${
              speaking
                ? "border-teal/50 bg-teal/10 text-teal"
                : "border-white/10 bg-white/[0.03] text-slatelight hover:border-white/25"
            }`}
          >
            <Volume2 className={`h-4 w-4 shrink-0 ${speaking ? "text-teal" : "text-slatemuted"}`} aria-hidden="true" />
            {speaking ? "Stop Reading" : "Read Aloud"}
            {!ttsSupported && <span className="ml-auto text-[11px] text-slatemuted">Not supported</span>}
          </button>

          <SectionLabel>A-ID</SectionLabel>
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Coming soon — connect your A-ID to apply your saved accessibility preferences"
            data-testid="a11y-aid-connect"
            className="flex w-full cursor-not-allowed items-center gap-2.5 rounded-xl border border-dashed border-teal/25 bg-teal/[0.04] px-3 py-2.5 text-left opacity-70"
          >
            <Fingerprint className="h-4 w-4 shrink-0 text-teal" aria-hidden="true" />
            <span className="min-w-0 flex-1 text-sm font-medium text-slatelight">
              Use my A-ID accessibility preferences
            </span>
            <span className="shrink-0 rounded-md border border-cyan/40 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-cyan">
              Coming soon
            </span>
          </button>

          <button
            type="button"
            onClick={resetAll}
            data-testid="a11y-reset"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 px-3 py-2.5 text-sm font-semibold text-slatelight transition-colors hover:border-teal/50 hover:text-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset Settings
          </button>
        </div>
      )}
    </>
  );
};
